import { resolveContentLink } from "@/lib/contentLink";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calendar, ArrowRight, Clock, TrendingDown, Users, AlertTriangle, CheckCircle2, Scale } from "lucide-react";
import { EditableImage } from "@/components/editable/EditableImage";
import salarisrondeHero from "@/assets/images/salarisronde-hero-photo.webp";
import row2Nl from "@/assets/images/salarisronde-row2-nl.webp";
import row2En from "@/assets/images/salarisronde-row2-en.webp";
import row2De from "@/assets/images/salarisronde-row2-de.webp";
import row3Nl from "@/assets/images/salarisronde-row3-nl.webp";
import row3En from "@/assets/images/salarisronde-row3-en.webp";
import row3De from "@/assets/images/salarisronde-row3-de.webp";
import row1Nl from "@/assets/images/salarisronde-row1-nl.webp";
import row1En from "@/assets/images/salarisronde-row1-en.webp";
import row1De from "@/assets/images/salarisronde-row1-de.webp";
import { ClientLogos } from "@/components/ClientLogos";
import { ScheduleDemo } from "@/components/sections";
import { PartnerForChange } from "@/components/sections/PartnerForChange";
import { PageContainer } from "@/components/layouts/MainLayout";
import { T, THtml } from "@/components/editable/T";
import { getLocalizedRoutePath } from "@/lib/routeUtils";
import { FeatureImageFrame } from "@/components/FeatureImageFrame";

const P = "compensationCycle";

const featureColors = [
  "#F3EDFD", "#FDF2ED", "#FFFBEB"];


const Salarisronde = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'nl';
  const demoPath = getLocalizedRoutePath('demo', lang);

  const features = [
  {
    titleKey: "features.row1.title",
    titleDefault: "Merit-verdeling volgens vaste uitgangspunten",
    bodyKey: "features.row1.body",
    bodyDefault: "Verdeel het budget op basis van functieniveau, salarispositie en evaluatie in plaats van op gevoel.",
    imageKey: 'row1-image',
    color: featureColors[0],
    image: lang === 'de' ? row1De : lang === 'en' ? row1En : row1Nl,
    imageAlt: 'Functiematrix met functiefamilies, niveaus en salarisbanden',
    demoCta: true
  },
  {
    titleKey: "features.row2.title",
    titleDefault: "Stuur bij terwijl de ronde loopt",
    bodyKey: "features.row2.body",
    bodyDefault: "Budgetsturing tijdens het proces, dus je ziet direct wat een voorstel doet met de totale ruimte.",
    imageKey: 'row2-image',
    color: featureColors[1],
    image: lang === 'de' ? row2De : lang === 'en' ? row2En : row2Nl,
    imageAlt: 'Salarisronde met budgetsturing en onderbouwde voorstellen',
    demoCta: true
  },
  {
    titleKey: "features.row3.title",
    titleDefault: "Eén goedkeuringsflow in plaats van een mailwissel",
    bodyKey: "features.row3.body",
    bodyDefault: "Voorstellen lopen langs de juiste mensen met een vastgelegde status, zodat de ronde traceerbaar is.",
    imageKey: 'row3-image',
    color: featureColors[2],
    image: lang === 'de' ? row3De : lang === 'en' ? row3En : row3Nl,
    imageAlt: 'Onderbouwd salarisvoorstel met nieuwe beloning en toelichting',
    cta: { labelKey: "features.row3.cta", labelDefault: "Ontdek beloningstransparantie en equal pay", link: resolveContentLink(`/${lang}/platform/compensation/beloningstransparantie-equal-pay/`, lang) }
  }];


  return (
    <>
      {/* Hero Section */}
      <section className="pt-[140px] pb-6 px-6 relative">
        <PageContainer className="relative z-10">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to={`/${lang}/`} className="hover:text-primary transition-colors">
              <T page={P} k="breadcrumb.home" d="Home" />
            </Link>
            <span>›</span>
            <Link to={`/${lang}/platform/compensation/`} className="hover:text-primary transition-colors">
              <T page={P} k="breadcrumb.parent" d="Compensation" />
            </Link>
            <span>›</span>
            <span style={{ color: "#2A2334" }}>
              <T page={P} k="breadcrumb.current" d="Salarisronde" />
            </span>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-12">
            <div className="animate-fade-in">
              <p className="text-lead text-muted-foreground mb-4">
                <T page={P} k="hero.subtitle" d="Compensation cycle van voorstel tot goedkeuring" />
              </p>
              <h1 className="mb-6" style={{ color: '#2A2334' }}>
                <T page={P} k="hero.title.lead" d="Een salarisronde" />{' '}
                <T page={P} k="hero.title.accent" d="die op tijd en kloppend klaar is" as="span" />
              </h1>
              <THtml page={P} k="hero.body" d="Verdeel budget eerlijk over je team, en laat elk voorstel automatisch de juiste goedkeuring doorlopen." as="div" className="text-lead text-muted-foreground mb-8 max-w-xl" />
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="text-sm px-6 py-5 rounded-full bg-[#9055EE] hover:bg-[#753DCE]" onClick={() => window.open('https://app.learned.io/register', '_blank')}>
                  <T page={P} k="hero.tryFree" d="Probeer gratis" />
                </Button>
                <Button variant="outline" size="lg" className="text-sm px-6 py-5 rounded-full bg-white hover:bg-gray-50" onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}>
                  <Calendar className="mr-2 h-4 w-4" style={{ color: '#9055EE' }} />
                  <T page={P} k="hero.planDemo" d="Plan een demo" />
                </Button>
              </div>
            </div>

            <div className="animate-fade-in relative" style={{ animationDelay: '200ms' }}>
              <EditableImage
                page={P}
                section="hero"
                imageKey="hero-image"
                src={salarisrondeHero}
                alt="Medewerker werkt aan de salarisronde achter zijn bureau"
                className="w-full h-[450px] object-cover object-center rounded-2xl"
                loading="eager"
              />
              <div className="absolute -right-8 -top-5 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform rotate-3 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1100ms', animationFillMode: 'forwards' }}>
                <Clock className="w-4 h-4 text-[#9055EE]" />
                <T page={P} k="hero.cards.onTime" d="Ronde op tijd afgerond" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
              <div className="absolute -right-6 bottom-24 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform -rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1400ms', animationFillMode: 'forwards' }}>
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <T page={P} k="hero.cards.noExcel" d="Zonder spreadsheets" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
              <div className="absolute -left-6 bottom-10 bg-white rounded-full shadow-xl ring-[8px] ring-white/80 px-4 py-2.5 transform -rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-2 opacity-0 animate-fade-float-in" style={{ animationDelay: '1700ms', animationFillMode: 'forwards' }}>
                <Scale className="w-4 h-4 text-[#FB9A24]" />
                <T page={P} k="hero.cards.explainable" d="Elk voorstel uitlegbaar" as="span" className="text-sm font-semibold text-gray-700" />
              </div>
            </div>
          </div>
          <ClientLogos />
        </PageContainer>
      </section>

      {/* Challenge Block */}
      <section
        className="overflow-x-hidden px-4 py-20 sm:px-6"
        style={{ background: "linear-gradient(135deg, #2A2334 0%, #4A3660 50%, #2A2334 100%)" }}
      >
        <div className="mx-auto w-full max-w-5xl">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:justify-center sm:gap-4">
              {[
                { icon: Clock, k: "challenges.pain1", d: "Rondes die weken duren" },
                { icon: TrendingDown, k: "challenges.pain2", d: "Spreadsheets zonder actuele versie" },
                { icon: Users, k: "challenges.pain3", d: "Voorstellen die niemand kan uitleggen" },
                { icon: AlertTriangle, k: "challenges.pain4", d: "Budget dat halverwege niet meer klopt" },
              ].map((point, i) => {
                const Icon = point.icon;
                return (
                  <div
                    key={i}
                    className="flex w-full items-center justify-center gap-3 rounded-full bg-white/10 px-4 py-3 backdrop-blur-sm sm:w-auto sm:px-5"
                  >
                    <Icon className="w-4 h-4 text-white/60 shrink-0" />
                    <T page={P} k={point.k} d={point.d} as="span" className="text-center text-sm leading-snug text-white/80" />
                  </div>
                );
              })}
            </div>

            <div>
              <T page={P} k="challenges.eyebrow" d="De uitdaging" as="p" className="text-xs font-semibold uppercase tracking-wider text-white/60 mb-2" />
              <h2 className="feature-section-heading text-white mb-6">
                <T page={P} k="challenges.title" d="Tijdsintensieve rondes, zonder gefundeerde uitkomst" />
              </h2>
              <div className="space-y-4">
                <THtml
                  page={P}
                  k="challenges.body1"
                  d="Een salarisronde is een van de weinige momenten waarop een organisatie letterlijk laat zien wat ze waardeert. Toch wordt die beslissing vaak genomen in een spreadsheet die van manager naar HR naar directie reist, waarbij niemand meer precies kan vertellen waarom de ene medewerker meer kreeg dan de andere."
                  as="p"
                  className="text-sm text-white/80 leading-relaxed"
                />
                <THtml
                  page={P}
                  k="challenges.body2"
                  d="Het gevolg raakt de kern van wat een organisatie bindt: medewerkers die twijfelen aan hun beloning, verliezen vertrouwen in de hele organisatie. En zodra niemand meer kan uitleggen waar een bedrag vandaan komt, wordt beloning een risico in plaats van een instrument voor motivatie en waardering."
                  as="p"
                  className="text-sm text-white/80 leading-relaxed"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="pt-[60px] pb-20 px-6">
        <PageContainer>
          <div className="space-y-16">
            {features.map((feature, index) => {
              const isReversed = index % 2 === 1;
              return (
                <div
                  key={index}
                  className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}>

                  <div className={`${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                    <h2 className="feature-section-heading text-foreground">
                      <T page={P} k={feature.titleKey} d={feature.titleDefault} />
                    </h2>
                    <THtml page={P} k={feature.bodyKey} d={feature.bodyDefault} as="p" className="text-lead text-muted-foreground" />

                    {(feature.cta || feature.demoCta) &&
                    <Button asChild className="mt-6 rounded-full bg-[#9055EE] hover:bg-[#753DCE] text-white">
                        <Link to={demoPath}>
                          <T page={P} k="features.demoCta" d="Plan een demo" />
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    }
                  </div>
                  <div className={`${isReversed ? 'lg:order-1' : 'lg:order-2'}`}>
                    {feature.image ? (
                      <EditableImage
                        page={P}
                        section="features"
                        imageKey={feature.imageKey}
                        src={feature.image}
                        alt={feature.imageAlt}
                        className="w-full rounded-3xl"
                      />
                    ) : (
                      /* TODO: echte productafbeelding toevoegen (Visuals: section features) */
                      <FeatureImageFrame color={feature.color} />
                    )}
                  </div>
                </div>);

            })}
          </div>
        </PageContainer>
      </section>


      <PartnerForChange />

      <ScheduleDemo />
    </>);

};

export default Salarisronde;
